import React from 'react';
import { defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { PrimaryActionButton } from '../../components/shipment-configuration-widget/primary-action-button';
import { CSWTypes, CSWTypeValues } from '../../enums/csw-types';
import { useFulfillmentPlannerContext } from '../../fulfillment-planner/context';
import { useFulfillmentPlannerCreateLabel } from '../../fulfillment-planner/rate-and-create-label/use-fulfillment-planner-create-label';
import { useContainsDemoOrder } from '../../demos/hooks/use-contains-demo-order';
import Global from '../../store/global-selectors';
import { Providers } from '../../enums/providers';
import { EnsureSerialNumberProvider } from '../../serial-numbers/ensure-serial-number-provider';
import { useFulfillmentPlanSerializableItems } from '../../serial-numbers/enforcement/use-fulfillment-plan-serializable-items';
import { useSerialPrintGuard } from '../../serial-numbers/enforcement/use-serial-print-guard';
import { SerialWarningModal } from '../../serial-numbers/enforcement/serial-warning-modal';
import { messages as serialEnforcementMessages } from '../../serial-numbers/enforcement/messages';

const CREATE_AND_PRINT_LABEL = 0;
const CREATE_TEST_LABEL = 2;

const messages = defineMessages({
  disabledTooltip: {
    description: 'tooltip message for when the print button is disabled',
    defaultMessage: 'Please Select any USPS service to enable Test Label',
    id: 'jbC8nX',
  },
});

const useGetActions = (isDemoOrder) => {
  const { cswTypeId } = useFulfillmentPlannerContext();
  const cswType = CSWTypeValues.find((t) => t.id === cswTypeId) || CSWTypes.standard;
  const cswTypePrimaryActions = cswType.primaryActions;
  const userSettingActionId = useSelector((state) =>
    Global.selectUserUserSetting(state, {
      userSetting: 'v3.configureShipmentWidget.actionButton',
    }),
  );

  const defaultPrimaryActionId = isDemoOrder
    ? cswTypePrimaryActions[CREATE_TEST_LABEL]?.id
    : userSettingActionId || cswTypePrimaryActions[CREATE_AND_PRINT_LABEL]?.id;

  return {
    defaultPrimaryActionId,
    primaryActions: cswTypePrimaryActions,
  };
};

// useSerialPrintGuard reads captured serials from SerialNumberProvider's context, which isn't
// guaranteed to be mounted by an ancestor — this container is reached (via RateAndCreateLabel)
// from the orders drawer, shipment-details modal, and create-label flows, none of which provide
// one. Without it, getSerialNumbers falls back to an empty list for every item, so the guard
// treats every serial-required item as missing regardless of what's actually on file.
// EnsureSerialNumberProvider fills that gap without shadowing the provider order details already
// mounts. A component can't consume its own children's context, so this has to wrap, not be
// wrapped by, the guard's consumer.
export const PrimaryActionButtonContainer = (props) => {
  const { fulfillmentPlanId } = useFulfillmentPlannerContext();

  return (
    <EnsureSerialNumberProvider fulfillmentPlanId={fulfillmentPlanId}>
      <PrimaryActionButtonContent {...props} fulfillmentPlanId={fulfillmentPlanId} />
    </EnsureSerialNumberProvider>
  );
};

PrimaryActionButtonContainer.propTypes = {
  buttonClassName: PropTypes.string,
  buttonContainerClassName: PropTypes.string,
  buttonLabelClassName: PropTypes.string,
  disabled: PropTypes.bool,
};

const PrimaryActionButtonContent = ({
  buttonClassName,
  buttonContainerClassName,
  buttonLabelClassName,
  disabled = false,
  fulfillmentPlanId,
}) => {
  const theme = THEME_CONSTANTS.primary;
  const isDemoOrder = useContainsDemoOrder([fulfillmentPlanId]);
  const { defaultPrimaryActionId, primaryActions } = useGetActions(isDemoOrder);
  const selectedSellerLabelProviderId = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'labelConfiguration.sellerLabelProviderId',
    }),
  );
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const uspsSellerLabelProviderId = sellerLabelProviders.find(
    (sellerLabelProvider) => sellerLabelProvider.labelProviderId === Providers.Stamps.labelProviderId,
  )?.sellerLabelProviderId;
  const isUspsSelected = selectedSellerLabelProviderId === uspsSellerLabelProviderId;
  const testLabelShouldBeDisabled = !isUspsSelected && isDemoOrder && selectedSellerLabelProviderId !== null;
  const { isCreateLabelDisabled } = useFulfillmentPlannerCreateLabel();

  const serializableItems = useFulfillmentPlanSerializableItems(fulfillmentPlanId);
  const serialPrintGuard = useSerialPrintGuard(serializableItems);

  const disablePrintButton = testLabelShouldBeDisabled || disabled || isCreateLabelDisabled || serialPrintGuard.isPrintBlocked;

  let disabledTooltipMessage = null;
  if (testLabelShouldBeDisabled) {
    disabledTooltipMessage = <Text textType={TextType.Caption} value={messages.disabledTooltip} />;
  } else if (serialPrintGuard.isPrintBlocked) {
    disabledTooltipMessage = <Text textType={TextType.Caption} value={serialEnforcementMessages.printBlockedTooltip} />;
  }

  return (
    <>
      <PrimaryActionButton
        buttonClassName={buttonClassName}
        buttonContainerClassName={buttonContainerClassName}
        buttonLabelClassName={buttonLabelClassName}
        defaultPrimaryActionId={defaultPrimaryActionId}
        disabled={disablePrintButton}
        disabledTooltipMessage={disabledTooltipMessage}
        guardAction={serialPrintGuard.guardPrint}
        isLightTheme={!!testLabelShouldBeDisabled}
        primaryActions={primaryActions}
        theme={theme}
      />
      {serialPrintGuard.isWarningModalOpen && (
        <SerialWarningModal onCancel={serialPrintGuard.dismissWarning} onProceed={serialPrintGuard.confirmWarningAndProceed} />
      )}
    </>
  );
};

PrimaryActionButtonContent.propTypes = {
  buttonClassName: PropTypes.string,
  buttonContainerClassName: PropTypes.string,
  buttonLabelClassName: PropTypes.string,
  disabled: PropTypes.bool,
  fulfillmentPlanId: PropTypes.string,
};
